import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import { saveMediaLibraryItem, StorageExceededError } from '$/api/canvasRquest';
import type { MediaLibraryItemSaveParams, MediaLibraryMaterialType } from '$/api/server.types';
import { useLanguageStore } from '$/store/languageStore';
import { sanitizeFilename } from '$/utils/download';
import {
  blobToFile,
  dealUploadFile,
  getVideoCoverUrl,
  type CallbackFiles,
} from '$/utils/upload';
import { NODE_NAME_CONFIG, TVFlowNodeNextTypeEnum } from '../config/node.config';
import { resolveTeamFlowNodeContentSource } from './flowNodeTrack';

type LooseRecord = Record<string, any>;

interface TeamFlowMediaLibraryNodeData extends LooseRecord {
  inputNodeId?: LooseRecord;
  mediaLibraryItemId?: number | string;
  media_list?: LooseRecord[];
  mcpInfo?: LooseRecord;
  name?: string;
  title?: string;
}

interface TeamFlowMediaLibraryResource extends LooseRecord {
  materialType: MediaLibraryMaterialType;
}

export interface SaveTeamFlowNodeToMediaLibraryOptions {
  folderId: number;
  name: string;
  node: FlowNodeEntity;
  prebuiltParams?: MediaLibraryItemSaveParams;
}

export interface SaveTeamFlowNodeToMediaLibraryResult {
  itemId: number;
  params: MediaLibraryItemSaveParams;
}

const DEFAULT_MEDIA_LIBRARY_NAME_MAP: Partial<Record<TVFlowNodeNextTypeEnum, string>> = {
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: '文本',
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: '图片',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: '视频',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: '音频',
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: '脚本',
};

/** 将任意文本按 rune 长度截断，避免中文被 String.slice 截坏。 */
function truncateByRune(value: string, maxLength: number): string {
  return Array.from(value).slice(0, maxLength).join('');
}

/** 读取字符串字段并去除首尾空白。 */
function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

/** 将未知值解析为正数，非法值返回 undefined。 */
function normalizePositiveNumber(value: unknown, round = false): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return round ? Math.round(n) : n;
}

/** 去掉 URL 查询串后提取小写扩展名。 */
function getUrlExtension(url?: string): string {
  const path = (url || '').split(/[?#]/)[0] ?? '';
  const filename = path.split('/').pop() ?? '';
  const ext = filename.includes('.') ? filename.split('.').pop() : '';
  return ext?.toLowerCase?.().replace(/^\./, '').trim() || '';
}

/** 序列化 JSON 字符串，失败时返回空字符串避免阻塞保存主流程。 */
function safeStringifyJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return '';
  }
}

/** 根据节点类型映射新素材库 material_type，文本与脚本保持独立枚举。 */
function resolveMediaLibraryMaterialType(flowNodeType: string | number): MediaLibraryMaterialType | null {
  if (flowNodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE) return 'image';
  if (flowNodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE) return 'video';
  if (flowNodeType === TVFlowNodeNextTypeEnum.AUDIO_NODE) return 'audio';
  if (flowNodeType === TVFlowNodeNextTypeEnum.TEXT_NODE) return 'text';
  if (flowNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE) return 'script';
  return null;
}

/** 读取节点展示名，过滤配置 key，作为弹窗默认素材名。 */
export function getTeamFlowNodeDefaultMediaLibraryName(node: FlowNodeEntity): string {
  const data = (node.toJSON?.()?.data ?? {}) as TeamFlowMediaLibraryNodeData;
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const displayName = pickString(data.title, data.name);
  const configName = NODE_NAME_CONFIG[nodeType];
  const fallback = DEFAULT_MEDIA_LIBRARY_NAME_MAP[nodeType] || '素材';
  const normalizedName = displayName && displayName !== configName ? displayName : fallback;
  return truncateByRune(normalizedName, 20);
}

/** 从节点首个有效 media 中构造新素材库保存资源。 */
function getTeamFlowNodeMediaLibraryResource(
  node: FlowNodeEntity,
  data: TeamFlowMediaLibraryNodeData,
): TeamFlowMediaLibraryResource | null {
  const materialType = resolveMediaLibraryMaterialType(node.flowNodeType);
  if (!materialType) return null;
  const mediaList = Array.isArray(data.media_list)
    ? data.media_list.map((media) => {
      if (!isPlainRecord(media)) return media;
      const sanitizedMedia = { ...media };
      delete sanitizedMedia.compress_url;
      delete sanitizedMedia.compressUrl;
      delete sanitizedMedia.watermark_compress_url;
      delete sanitizedMedia.watermarkCompressUrl;
      return sanitizedMedia;
    })
    : [];
  const firstMedia = mediaList.find((media) => {
    if (materialType === 'text' || materialType === 'script') {
      return Boolean(pickString(media.url, media.originUrl, media.uri) || pickString(media.value));
    }
    return Boolean(pickString(media.originUrl, media.url, media.uri));
  });
  if (!firstMedia) return null;
  return {
    ...firstMedia,
    materialType,
  };
}

/** 判断节点是否有可保存到新素材库的内容。 */
export function isTeamFlowNodeMediaLibrarySavable(node?: FlowNodeEntity | null): boolean {
  if (!node) return false;
  const data = (node.toJSON?.()?.data ?? {}) as TeamFlowMediaLibraryNodeData;
  return Boolean(getTeamFlowNodeMediaLibraryResource(node, data));
}

/** 上传文本/脚本正文为 markdown 文件，返回上传后的文件信息。 */
function uploadMediaLibraryMarkdownContent(value: unknown, name: string): Promise<CallbackFiles | null> {
  if (typeof value !== 'string' || !value.trim()) {
    return Promise.resolve(null);
  }
  const fileName = `${sanitizeFilename(name) || '素材'}.md`;
  const blob = new Blob([value], { type: 'text/markdown' });
  const file = blobToFile(blob, fileName);
  return new Promise((resolve, reject) => {
    dealUploadFile({
      files: [file],
      controlFilesOptions: {
        accept: ['text/markdown'],
        importCount: 1,
      },
      isCheckSafe: false,
      location: 'workflow_teams_node',
    }, {
      callback: (files) => resolve(files[0] ?? null),
      onUploadError: (_errorList, errorMessage) => reject(errorMessage),
    });
  });
}

/** 判断值是否为普通对象，供 gen_params 组装时过滤数组和空值。 */
function isPlainRecord(value: unknown): value is LooseRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 仅在值有意义时写入 gen_params，避免保存一堆空字段。 */
function assignMediaLibraryGenParamIfPresent(target: LooseRecord, key: string, value: unknown) {
  if (value == null || value === '') return;
  if (Array.isArray(value) && value.length === 0) return;
  if (isPlainRecord(value) && Object.keys(value).length === 0) return;
  // 集中累积 gen_params 字段，调用处传入同一个对象避免多次展开复制。
  // eslint-disable-next-line no-param-reassign
  target[key] = value;
}

/** 构造素材库 gen_params，用于再次回灌画布时恢复提示词、媒体列表和节点上下文。 */
function buildMediaLibraryGenParams(data: TeamFlowMediaLibraryNodeData): string | undefined {
  const mcpInfo = isPlainRecord(data.mcpInfo) ? data.mcpInfo : undefined;
  const mediaList = Array.isArray(data.media_list) ? data.media_list : [];
  const nodeData: LooseRecord = {};
  assignMediaLibraryGenParamIfPresent(nodeData, 'name', data.name);
  assignMediaLibraryGenParamIfPresent(nodeData, 'title', data.title);
  assignMediaLibraryGenParamIfPresent(nodeData, 'action', data.action);
  assignMediaLibraryGenParamIfPresent(nodeData, 'inputNodeId', data.inputNodeId);
  assignMediaLibraryGenParamIfPresent(nodeData, 'taskInfo', data.taskInfo);
  assignMediaLibraryGenParamIfPresent(nodeData, 'tool', data.tool);
  assignMediaLibraryGenParamIfPresent(nodeData, 'mcpInfo', mcpInfo);
  assignMediaLibraryGenParamIfPresent(nodeData, 'media_list', mediaList);

  const genParams: LooseRecord = {};
  assignMediaLibraryGenParamIfPresent(genParams, 'mcpInfo', mcpInfo);
  assignMediaLibraryGenParamIfPresent(genParams, 'parameters', mcpInfo?.parameters);
  assignMediaLibraryGenParamIfPresent(genParams, 'media_list', mediaList);
  assignMediaLibraryGenParamIfPresent(genParams, 'node_data', nodeData);
  if (!Object.keys(genParams).length) {
    return undefined;
  }
  return safeStringifyJson(genParams) || undefined;
}

/** 构造素材库 ext JSON，保存资源尺寸、时长、来源任务、内容来源和水印等扩展字段。 */
function buildMediaLibraryExt(
  resource: TeamFlowMediaLibraryResource,
  url: string,
  contentSource: string,
): string {
  const ext = {
    file_extension: pickString(resource.ext, resource.suffix) || getUrlExtension(url),
    width: normalizePositiveNumber(resource.width, true),
    height: normalizePositiveNumber(resource.height, true),
    duration: normalizePositiveNumber(resource.duration),
    task_id: pickString(resource.task_id, resource.taskId),
    sub_task_id: pickString(resource.sub_task_id, resource.subTaskId),
    source_id: pickString(resource.source_id, resource.sourceId, resource.task_id, resource.taskId),
    // 持久化内容来源，供素材库卡片曝光 / 添加埋点回读 source_from。
    content_source: contentSource,
    watermark_url: pickString(resource.watermark_url, resource.watermarkUrl),
    watermark_cover_url: pickString(resource.watermark_cover_url, resource.watermarkCoverUrl),
  };
  return safeStringifyJson(ext);
}

/** 读取主文件大小，供 storage/usage 正确统计。 */
function getMediaLibraryFileSize(resource: TeamFlowMediaLibraryResource, uploaded?: CallbackFiles | null): number | undefined {
  return normalizePositiveNumber(
    resource.file_size
    ?? resource.fileSize
    ?? resource.fileByte
    ?? resource.file_byte
    ?? resource.fileBytes
    ?? resource.bytes
    ?? resource.size
    ?? resource.file?.size
    ?? uploaded?.size,
    true,
  );
}

/** 从 Content-Range 响应头解析远程文件总大小。 */
function parseContentRangeTotalSize(value: string | null): number | undefined {
  const total = value?.match(/\/(\d+)$/)?.[1];
  return normalizePositiveNumber(total, true);
}

/** 从 Content-Length 响应头读取远程文件大小。 */
function readContentLengthHeader(headers: Headers): number | undefined {
  return normalizePositiveNumber(headers.get('content-length'), true);
}

/** 当节点历史数据缺少 size 时，尝试通过远程资源响应头补齐文件大小。 */
async function fetchRemoteFileSize(url: string): Promise<number | undefined> {
  if (typeof fetch !== 'function' || !url) return undefined;
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer = controller ? globalThis.setTimeout(() => controller.abort(), 3000) : undefined;
  try {
    const headResponse = await fetch(url, {
      method: 'HEAD',
      signal: controller?.signal,
    });
    const headSize = readContentLengthHeader(headResponse.headers);
    if (headSize) return headSize;
  } catch (_err) {
    // 部分资源服务不允许 HEAD 或跨域读取响应头，继续尝试 Range 请求。
  } finally {
    if (timer) globalThis.clearTimeout(timer);
  }

  try {
    const rangeResponse = await fetch(url, {
      headers: { Range: 'bytes=0-0' },
    });
    return parseContentRangeTotalSize(rangeResponse.headers.get('content-range'));
  } catch (_err) {
    return undefined;
  }
}

/** 构造 item/save 请求体；文本/脚本无 URL 时先上传 markdown。 */
export async function buildMediaLibraryItemSaveParamsFromNode(
  options: SaveTeamFlowNodeToMediaLibraryOptions,
): Promise<MediaLibraryItemSaveParams> {
  const { smartcatTranslationText } = useLanguageStore.getState().languageStore;
  const { node, folderId } = options;
  const data = (node.toJSON?.()?.data ?? {}) as TeamFlowMediaLibraryNodeData;
  const resource = getTeamFlowNodeMediaLibraryResource(node, data);
  const name = truncateByRune(options.name.trim(), 20);
  if (!resource) {
    throw new Error('当前节点暂无可保存内容');
  }
  if (!name) {
    throw new Error(smartcatTranslationText((v) => v.enter_name));
  }
  if (!Number.isFinite(Number(folderId)) || Number(folderId) <= 0) {
    throw new Error(smartcatTranslationText((v) => v.select_group));
  }

  const resourceUrl = pickString(resource.originUrl, resource.url, resource.uri);
  const uploaded = (resource.materialType === 'text' || resource.materialType === 'script') && !resourceUrl
    ? await uploadMediaLibraryMarkdownContent(resource.value, name)
    : null;
  const url = pickString(resourceUrl, uploaded?.url);
  if (!url) {
    throw new Error('当前节点暂无可保存内容');
  }

  const thumbnailUrl = pickString(
    resource.thumbnail_url,
    resource.thumbnailUrl,
    resource.cover_url,
    resource.coverUrl,
    resource.watermark_cover_url,
    resource.materialType === 'video' ? getVideoCoverUrl(url) : '',
  );

  const genParams = buildMediaLibraryGenParams(data);
  const ext = buildMediaLibraryExt(resource, url, resolveTeamFlowNodeContentSource(node));
  const fileSize = getMediaLibraryFileSize(resource, uploaded) ?? await fetchRemoteFileSize(url);

  return {
    folder_id: Number(folderId),
    name,
    material_type: resource.materialType,
    url,
    ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}),
    ...(genParams ? { gen_params: genParams } : {}),
    ...(ext ? { ext } : {}),
    ...(fileSize ? { file_size: fileSize } : {}),
  };
}

/** 将新素材库 item_id 回写到节点独立字段，避免覆盖旧 assetId 语义。 */
function writeMediaLibraryItemIdToTeamFlowNode(node: FlowNodeEntity, itemId: number) {
  const data = (node.toJSON?.()?.data ?? {}) as TeamFlowMediaLibraryNodeData;
  const mediaList = Array.isArray(data.media_list) ? data.media_list : [];
  const nextMediaList = mediaList.length
    ? mediaList.map((item, index) => (index === 0 ? { ...item, mediaLibraryItemId: itemId } : item))
    : mediaList;
  node.form?.updateFormValues({
    ...data,
    mediaLibraryItemId: itemId,
    media_list: nextMediaList,
  });
}

/** 保存单个 TeamFlow 节点到新素材库并返回 item_id。 */
export async function saveTeamFlowNodeToMediaLibrary(
  options: SaveTeamFlowNodeToMediaLibraryOptions,
): Promise<SaveTeamFlowNodeToMediaLibraryResult> {
  const params = options.prebuiltParams ?? await buildMediaLibraryItemSaveParamsFromNode(options);
  const { item_id: itemId } = await saveMediaLibraryItem(params);
  writeMediaLibraryItemIdToTeamFlowNode(options.node, itemId);
  return {
    itemId,
    params,
  };
}

export interface SaveTeamFlowNodesToMediaLibraryResult {
  successCount: number;
  failCount: number;
  total: number;
  /** 命中容量不足的错误，供调用方展示宿主容量拦截弹窗。 */
  storageExceededError?: StorageExceededError;
}

/**
 * 批量保存多个 TeamFlow 节点到新素材库；每个节点使用其默认名称，逐个提交并汇总成败。
 * 单个节点失败不阻断其它节点；命中容量不足时立即中止并回传该错误。
 */
export async function saveTeamFlowNodesToMediaLibrary(options: {
  nodes: FlowNodeEntity[];
  folderId: number;
}): Promise<SaveTeamFlowNodesToMediaLibraryResult> {
  const { nodes, folderId } = options;
  const savableNodes = nodes.filter((node) => isTeamFlowNodeMediaLibrarySavable(node));
  let successCount = 0;
  let failCount = 0;
  let storageExceededError: unknown;

  for (const node of savableNodes) {
    if (storageExceededError) {
      failCount += 1;
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop -- 顺序保存，便于容量不足时及时中止
      await saveTeamFlowNodeToMediaLibrary({
        node,
        folderId,
        name: getTeamFlowNodeDefaultMediaLibraryName(node),
      });
      successCount += 1;
    } catch (err) {
      if (err instanceof StorageExceededError) {
        storageExceededError = err;
      }
      failCount += 1;
    }
  }

  return {
    successCount,
    failCount,
    total: savableNodes.length,
    storageExceededError,
  };
}
